import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import "./FloatingButtons.css";

const SpeechRecognitionImpl = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

// Sleep utility function
const sleep = (seconds) =>
  new Promise((resolve) => setTimeout(resolve, seconds * 1000));

// Single reusable button component with simplified props
const FloatingButton = ({ text, onClick, x, y, disappearing, isSelected, isDisabled }) => (
  <button
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDisabled) {
        onClick();
      }
    }}
    onTouchEnd={(e) => {
      e.stopPropagation();
      if (!isDisabled) {
        onClick();
      }
    }}
    title={text}
    className={`floating-button ${disappearing ? "disappearing" : ""} ${isSelected ? "selected" : ""}`}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50px",
      cursor: isDisabled ? "not-allowed" : "pointer",
      background: isSelected
        ? "rgba(0, 255, 255, 0.3)"
        : "rgba(0, 0, 0, 0.2)",
      border: isSelected
        ? "3px solid #00ffff"
        : "2px solid #00ffff",
      color: "#00ffff",
      fontWeight: "600",
      fontSize: "14px",
      minWidth: "50px",
      height: "50px",
      padding: text ? "0 20px" : "0",
      position: "absolute",
      left: x,
      top: y,
      boxShadow: isSelected
        ? "0 0 20px rgba(0, 255, 255, 0.8), " +
        "0 0 40px rgba(0, 255, 255, 0.6), " +
        "0 0 60px rgba(0, 255, 255, 0.4), " +
        "inset 0 0 30px rgba(0, 255, 255, 0.3)"
        : "0 0 10px rgba(0, 255, 255, 0.5), " +
        "0 0 20px rgba(0, 255, 255, 0.3), " +
        "0 0 30px rgba(0, 255, 255, 0.1), " +
        "inset 0 0 20px rgba(0, 255, 255, 0.1)",
      textShadow: isSelected
        ? "0 0 15px rgba(0, 255, 255, 1)"
        : "0 0 10px rgba(0, 255, 255, 0.8)",
      backdropFilter: "blur(10px)",
      transition: "all 0.2s ease",
      transform: isSelected ? "scale(1.05)" : "scale(1)",
      zIndex: 1000,
      WebkitTapHighlightColor: "transparent",
      WebkitTouchCallout: "none",
      WebkitUserSelect: "none",
      userSelect: "none",
      touchAction: "manipulation",
      minHeight: "44px", // iOS minimum touch target
    }}
  >
    {text && <span style={{ whiteSpace: "nowrap" }}>{text}</span>}
  </button>
);

// Speech bubble component
const SpeechBubble = ({ text, large = false }) => {
  if (!text) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: "calc(var(--safe-area-top) + 84px)",
        transform: "translateX(-50%)",
        background: large ? "rgba(255, 255, 255, 0.85)" : "rgba(255, 255, 255, 0.8)",
        border: "1px solid rgba(204, 204, 204, 0.7)",
        borderRadius: "15px",
        padding: large ? "8px 20px" : "0px 15px",
        color: "black",
        fontSize: "18px",
        fontWeight: "500",
        width: large ? "calc(88% - 40px)" : "calc(80% - 40px)",
        minHeight: large ? "120px" : undefined,
        height: large ? "auto" : "120px",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
        zIndex: 1000,
        animation: "fadeInButton 0.3s ease-in",
        textOverflow: "ellipsis",
        lineHeight: large ? 1.4 : undefined,
      }}
    >
      {text}
      {/* Speech bubble tail */}
      <div
        style={{
          position: "absolute",
          bottom: "-12px",
          left: "75%",
          transform: "translateX(-50%)",
          width: "24px",
          height: "12px",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          clipPath: "polygon(50% 100%, 0% 0%, 100% 0%)",
          borderRadius: "4px 4px 0 0",
        }}
      />
    </div>
  );
};

const FloatingButtons = forwardRef(({ onButtonClick, onTextSubmit, voiceMode = false, woojuSpeaking = false, isProcessing = false, onListeningChange }, ref) => {
  const [buttons, setButtons] = useState([]);
  const [speechBubble, setSpeechBubble] = useState("");
  const [inlineInput, setInlineInput] = useState("");
  const [largeBubble, setLargeBubble] = useState(false);
  const [selectedButtonId, setSelectedButtonId] = useState(null);
  const [isProcessingClick, setIsProcessingClick] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const setListening = (val) => { setIsListening(val); onListeningChange?.(val); };
  const recognitionRef = useRef(null);
  const mountedRef = useRef(true);
  const startListeningFnRef = useRef(null);
  const accumulatedTranscriptRef = useRef("");

  // Wandering orb — picks a new personality spot each time Wooju finishes speaking
  const ORB_SPOTS = [
    { top: "10%",   left: "50%",  label: "halo"           }, // floating crown above her
    { top: "28%",   left: "14%",  label: "left shoulder"  },
    { top: "28%",   left: "86%",  label: "right shoulder" },
    { top: "48%",   left: "10%",  label: "left hand"      },
    { top: "48%",   left: "90%",  label: "right hand"     },
    { top: "62.2%", left: "49.9%",label: "crystal ball"   }, // exact ball center
    { top: "80%",   left: "30%",  label: "lower left"     },
    { top: "80%",   left: "70%",  label: "lower right"    },
  ];
  const lastSpotRef = useRef(5); // start on crystal ball
  const [orbSpot, setOrbSpot] = useState(ORB_SPOTS[5]);

  useEffect(() => {
    if (woojuSpeaking) return; // only wander when Wooju stops speaking
    let nextIdx;
    do { nextIdx = Math.floor(Math.random() * ORB_SPOTS.length); }
    while (nextIdx === lastSpotRef.current);
    lastSpotRef.current = nextIdx;
    setOrbSpot(ORB_SPOTS[nextIdx]);
  }, [woojuSpeaking]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recognitionRef.current?.stop();
    };
  }, []);

  // Auto-speak speech bubble text via TTS in voice mode
  useEffect(() => {
    if (!voiceMode || !speechBubble) return;
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(speechBubble);
    window.speechSynthesis.speak(utterance);
    return () => window.speechSynthesis.cancel();
  }, [speechBubble, voiceMode]);

  const toggleListening = () => {
    if (!SpeechRecognitionImpl) return;
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    window.speechSynthesis?.cancel();
    accumulatedTranscriptRef.current = "";
    const recognition = new SpeechRecognitionImpl();
    recognition.lang = "en-US";
    recognition.continuous = true;   // keep listening through natural pauses
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => mountedRef.current && setListening(true);
    recognition.onend = () => {
      if (!mountedRef.current) return;
      setListening(false);
      const final = accumulatedTranscriptRef.current.trim();
      accumulatedTranscriptRef.current = "";
      if (final && onButtonClick) onButtonClick(final);
    };
    recognition.onerror = (e) => {
      if (!mountedRef.current) return;
      setListening(false);
      // Still send whatever we captured before the error
      const partial = accumulatedTranscriptRef.current.trim();
      accumulatedTranscriptRef.current = "";
      if (partial && onButtonClick) onButtonClick(partial);
    };
    recognition.onresult = (event) => {
      // Accumulate all final phrases — don't send until recognition ends
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          accumulatedTranscriptRef.current += event.results[i][0].transcript + " ";
        }
      }
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  // Keep startListeningFnRef current so the imperative handle never has a stale closure
  startListeningFnRef.current = () => {
    if (SpeechRecognitionImpl && !isListening) toggleListening();
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    startListening: () => { startListeningFnRef.current?.(); },
    toggleListening: () => { toggleListening(); },
    // New consolidated function that sets buttons and speech bubble with animations
    setButtonsAndSpeechBubble: async (buttonTexts, bubbleText, options = {}) => {
      // Clear existing buttons and speech bubble
      if (buttons.length > 0) {
        setButtons((prev) =>
          prev.map((btn) => ({ ...btn, disappearing: true }))
        );
        // Shorten clear delay to keep total under 2s
        await sleep(0.2);
        setButtons([]);
      }
      setSpeechBubble("");
      setLargeBubble(!!options.large);
      setSelectedButtonId(null); // Reset selection

      // Set speech bubble first
      if (bubbleText) {
        setSpeechBubble(bubbleText);
      }

      // Add buttons with delays
      if (buttonTexts && buttonTexts.length > 0) {
        // Target: ensure the last button appears ~1.5s after call
        const TOTAL_SECONDS = 1.5;
        const CLEAR_DELAY = 0.2; // must match the clear sleep above
        const gaps = Math.max(1, buttonTexts.length - 1);
        const perGapSeconds = Math.max(0, (TOTAL_SECONDS - CLEAR_DELAY) / gaps);

        // Create all buttons at once to prevent duplicates
        const newButtons = [];
        for (let i = 0; i < buttonTexts.length; i++) {
          const buttonText = buttonTexts[i];
          let x, y;
          let attempts = 0;
          const maxAttempts = 50; // Prevent infinite loops

          do {
            // Safari-safe positioning with proper bounds checking
            const maxX = Math.max(0, window.innerWidth - 200);
            const maxY = Math.max(0, window.innerHeight - 120);
            const minY = bubbleText ? Math.min(440, maxY - 80) : 0;

            x = Math.random() * maxX;
            y = Math.random() * (maxY - minY) + minY;

            // Ensure buttons don't overlap - reduced distance requirement
            let anyClose = false;
            for (let j = 0; j < newButtons.length; j++) {
              const distance = Math.sqrt(
                Math.pow(newButtons[j].x - x, 2) + Math.pow(newButtons[j].y - y, 2)
              );
              // Reduced from 120px to 80px for better positioning
              if (distance < 80) {
                anyClose = true;
                break;
              }
            }
            if (!anyClose) {
              break;
            }
            attempts++;
          } while (attempts < maxAttempts);

          // If we couldn't find a perfect spot, just place it anyway
          newButtons.push({
            text: buttonText,
            id: `${Date.now()}-${i}-${Math.random()}`,
            x,
            y
          });
        }

        // Set all buttons at once
        setButtons(newButtons);
      }
    },
    clear: async () => {
      if (buttons.length > 0) {
        setButtons((prev) =>
          prev.map((btn) => ({ ...btn, disappearing: true }))
        );

        await sleep(0.5);

        setButtons([]);
      }
      setSpeechBubble("");
      setSelectedButtonId(null); // Reset selection
    },
  }));

  const handleButtonClick = (button) => {
    // Prevent multiple rapid clicks
    if (isProcessingClick) {
      return;
    }

    setIsProcessingClick(true);
    setSelectedButtonId(button.id);

    // Call the original handler after a short delay to show the selection
    // Safari needs a bit more time to process the visual feedback
    setTimeout(() => {
      if (onButtonClick) {
        onButtonClick(button.text);
      }
      // Reset processing state after a longer delay to prevent rapid clicks
      setTimeout(() => {
        setIsProcessingClick(false);
      }, 500);
    }, 300); // Increased delay for Safari
  };

  const shouldShowInlineInput = !voiceMode && speechBubble && buttons.length === 0;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        zIndex: 999,
        pointerEvents: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <SpeechBubble text={speechBubble} large={largeBubble} />
      {/* In voice mode: wandering orb — glides to a new spot after each response */}
      {voiceMode && (() => {
        const orbState = isListening ? "listening" : isProcessing ? "processing" : woojuSpeaking ? "speaking" : "ready";
        const orbLabel = isListening ? "listening…" : isProcessing ? "thinking…" : woojuSpeaking ? "speaking…" : "tap to speak";
        const canTap = orbState === "ready" || orbState === "listening";
        return (
          <div style={{
            position: "absolute",
            left: orbSpot.left,
            top: orbSpot.top,
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            zIndex: 1001,
            transition: "top 0.9s cubic-bezier(0.34, 1.56, 0.64, 1), left 0.9s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}>
            <button
              className={`mic-orb ${orbState}`}
              onClick={canTap ? toggleListening : undefined}
              aria-label={orbLabel}
            >
              {isListening ? "🛑" : isProcessing ? "✨" : woojuSpeaking ? "🔊" : "🎙️"}
            </button>
            <span className={`mic-label ${orbState}`}>{orbLabel}</span>
          </div>
        );
      })()}
      {!voiceMode && buttons.map((button) => (
        <FloatingButton
          key={button.id}
          text={button.text}
          onClick={() => handleButtonClick(button)}
          x={button.x}
          y={button.y}
          disappearing={button.disappearing}
          isSelected={selectedButtonId === button.id}
          isDisabled={isProcessingClick}
        />
      ))}
      {shouldShowInlineInput && (
        <form
          className="chat-input-container"
          onSubmit={(e) => {
            e.preventDefault();
            const value = inlineInput.trim();
            if (!value) return;
            if (onTextSubmit) {
              onTextSubmit(value);
            } else if (onButtonClick) {
              onButtonClick(value);
            }
            setInlineInput("");
          }}
          style={{
            position: "absolute",
            left: "50%",
            bottom: "calc(var(--safe-area-bottom) + 24px)",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            // Responsive width for different screen sizes - adjusted for FloatingButtons layout
            width: window.innerWidth <= 430
              ? "92vw"
              : window.innerWidth <= 767
                ? "95vw"
                : "min(680px, 86%)",
            maxWidth: window.innerWidth <= 430
              ? "380px"
              : window.innerWidth <= 767
                ? "420px"
                : "680px",
            background: "rgba(0, 0, 0, 0.2)",
            border: "none",
            borderRadius: 50,
            boxShadow:
              "0 0 10px rgba(0, 255, 255, 0.5), 0 0 20px rgba(0, 255, 255, 0.3), 0 0 30px rgba(0, 255, 255, 0.1), inset 0 0 20px rgba(0, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            padding: "6px 8px 6px 12px",
            zIndex: 1001,
          }}
        >
          <input
            type="text"
            value={inlineInput}
            onChange={(e) => setInlineInput(e.target.value)}
            placeholder="ask anything…"
            style={{
              flex: 1,
              padding: "0.8rem 1rem",
              fontSize: window.innerWidth <= 767 ? "0.9rem" : "1rem",
              fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: 30,
              color: "#111",
              outline: "none",
              minWidth: 0, // Allow input to shrink
              maxWidth: window.innerWidth <= 767 ? "calc(100% - 80px)" : "100%", // Leave space for send button
            }}
          />
          <button
            type="submit"
            className="neon-button float-slow"
            style={{
              fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
              border: "none",
              minWidth: window.innerWidth <= 767 ? "60px" : "auto",
              fontSize: window.innerWidth <= 767 ? "0.85rem" : "1rem",
              padding: window.innerWidth <= 767 ? "0.6rem 0.8rem" : "0.8rem 1rem",
            }}
          >
            Send
          </button>
        </form>
      )}
    </div>
  );
});

export default FloatingButtons;
